import TenantUser, { UserType } from "entities/TenantUser";
import { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAsterisk } from "@fortawesome/free-solid-svg-icons";
import PasswordTooltip from "pages/Login/PasswordTooltip";

export type TenantUserFormData = Omit<TenantUser, "Id"> & Partial<Pick<TenantUser, "Id">>;

interface TenantUserFormModalProps {
  show: boolean;
  onHide: () => void;
  onFormSubmit: (tenantUser: TenantUserFormData) => void;
  initialData: TenantUser | null;
  createSuperAdmin?: boolean;
}

const TenantUserFormModal = ({ show, onHide, onFormSubmit, initialData, createSuperAdmin }: TenantUserFormModalProps) => {
  const { t } = useTranslation("parts");
  const isInEditMode = initialData !== null;
  const defaultState: Omit<TenantUserFormData, "Id"> = {
    UserName: "",
    Email: "",
    Roles: [createSuperAdmin ? "SuperAdmin" : "Admin"],
    UserType: 1,
    Password: ""
  };
  
  const [formData, setFormData] = useState<Omit<TenantUserFormData, "Id">>(defaultState);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (show) {
      setFormData(
        isInEditMode
          ? {
            UserName: initialData.UserName,
            Email: initialData.Email,
            Roles: initialData.Roles,
            UserType: initialData.UserType,
            Password: ""
          }
        : defaultState
      );
    setValidated(false);
    }
  }, [show, initialData, isInEditMode]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const isNumericField = typeof prev[name as keyof typeof prev] === "number";

      return {
        ...prev,
        [name]: isNumericField ? Number(value) : value
      };
    });
  };

  const isEntraIdApp = formData.UserType === UserType.EntraIdApp;
  const isExternalLoginOnly = formData.UserType !== UserType.SelfServiceUser;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();
    if (form.checkValidity() === false) {
      setValidated(true);
    } else {
      const submitData = { ...formData, Id: initialData?.Id };
      if (isInEditMode && !submitData.Password) {
        delete (submitData as any).Password;
      }

      if (isEntraIdApp) {
        submitData.Roles = ["Admin"];
      }

      onFormSubmit(submitData);
    }
  };

  const useSuperAdminText = createSuperAdmin && !isEntraIdApp;

  const userTypeOptions = Object.entries(UserType)
    .filter(([, value]) => typeof value === "number")
    .map(([key, value]) => ({
      label: t(`settings.forms.userType.${key}`, key),
      value: value as number
    }))
    .filter(option => option.value === UserType.EntraIdUser || option.value === UserType.EntraIdApp);

  return (
    <Modal show={show} onHide={onHide} centered autoFocus={false}>
      <Modal.Header closeButton>
        <Modal.Title>
          {isInEditMode ? t("settings.editEmployeesInfo") : ( useSuperAdminText ? t("settings.addNewSAUser") : t("settings.addNewAdminUser"))}
        </Modal.Title>
      </Modal.Header>
      <Form noValidate validated={validated} onSubmit={handleSubmit} autoComplete="off">
        <Modal.Body>
          <Form.Group className="mb-3" controlId="formUserType">
            <Form.Label>{t("settings.forms.userType")}</Form.Label>
            <Form.Select
              name="UserType"
              value={formData.UserType}
              onChange={handleChange}
              className="mb-2"
              >
                {userTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>{t(`settings.forms.userTypes.${option.label}`)}</option>
                ))}
              </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3" controlId="formUserName">
            <Form.Label>{ isEntraIdApp ?t("settings.app_registration_client_id") : t("settings.userName") } {<span className="ms-1 text-danger"><FontAwesomeIcon icon={faAsterisk} /></span>}</Form.Label>
            <Form.Control
                type="text"
                name="UserName"
                placeholder={createSuperAdmin ? t("settings.forms.enterEntraIdUserId") : t("settings.forms.enterUserName")}
                value={formData.UserName}
                onChange={handleChange}
                required
            />
            <Form.Control.Feedback type="invalid">
                {t("settings.forms.provideEmployeeName")}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" controlId="formEmployeeADAccount">
            <Form.Label>{isEntraIdApp ? t("settings.forms.contactEmail") : t("approvalWorkflows.values.Email")}</Form.Label>
            <Form.Control
              type="email"
              placeholder={isEntraIdApp ? t("settings.forms.enterContactEmail") : t("settings.forms.enterEmail")}
              name="Email"
              value={formData.Email}
              onChange={handleChange}
            />
          </Form.Group>
          { formData.UserType === UserType.SelfServiceUser && 
            <Form.Group className="mb-3" controlId="formPassword">
              <Form.Label>{t("pages:login.password")}</Form.Label>
              <PasswordTooltip id="formPassword">
                <Form.Control
                  type={"password"}
                  name="Password"
                  value={formData.Password || ""}
                  placeholder={isInEditMode ? t("settings.forms.leaveEmptyToKeepOldPassword") : ""}
                  onChange={handleChange}
                  required={!isExternalLoginOnly && !isInEditMode}
                  disabled={isExternalLoginOnly}
                />
              </PasswordTooltip>
              <Form.Control.Feedback type="invalid">
                {t("pages:login.errorPassword")}
              </Form.Control.Feedback>
            </Form.Group>
          }
          { useSuperAdminText && 
            <Form.Text>{isInEditMode ? t("settings.forms.globalAdminUsesExternalLogin") : t("settings.forms.externalLoginOnlyUserCreated")}</Form.Text>
          }
          { !isInEditMode &&
            <Form.Group><Form.Text>{createSuperAdmin ? t("settings.forms.userCreatedSuperAdmin") : t("settings.forms.userCreatedAdmin")}</Form.Text></Form.Group>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={onHide}>
            {t("common:form.buttons.cancel")}
          </Button>
          <Button variant="primary" type="submit">
            {t("common:form.buttons.save")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default TenantUserFormModal;